import React from "react";

import {
  Tab,
  Tabs,
  Stack,
  Typography,
  useTheme,
  Skeleton,
} from "@mui/material";
import toast from "react-hot-toast";

import MainLayout from "../../layouts/main-layout";
import { AuthContext } from "../../context";
import { useGetClient, useUpdateClient } from "../../store";
import ClientChip from "../../components/chips/client-chip";

import ClientAdminUsersTab from "./components/client-admin-users-tab";
import ClientGeneralTab from "./components/client-general-tab";
import ClientWhitelistingTab from "./components/client-whitelisting-tab";

const ClientPage: React.FC = () => {
  const theme = useTheme();
  const { clientId } = React.useContext(AuthContext);

  const { data: client = null, isLoading } = useGetClient(clientId);

  const [tabValue, setTabValue] = React.useState(0);

  const onUpdateSuccess = () => {
    toast.success("Client updated successfully");
  };

  const onUpdateError = (err: unknown) => {
    console.log(err);
    toast.error("Failed to update client");
  };

  const { mutate: updateClient, isPending } = useUpdateClient(
    onUpdateSuccess,
    onUpdateError,
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSaveGeneral = (clientName: string) => {
    if (!client) {
      return;
    }

    updateClient({
      clientId: client.clientId,
      input: {
        clientName,
      }
    });
  };

  const handleSaveWhitelist = (domainWhiteList: string[]) => {
    if (!client) {
      return;
    }

    updateClient({
      clientId: client.clientId,
      input: {
        domainWhiteList: domainWhiteList || [],
      }
    });
  };

  return (
    <MainLayout maxWidth="lg">
      <Stack spacing={2} pb={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={0.5}
          sx={{ pb: 1 }}
        >
          <Typography
            variant="h4"
            color={theme.palette.text.primary}
          >
            Client Settings
          </Typography>
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              width={150}
              height={40}
              sx={{ mb: 2 }}
            />
          ) : (
            <ClientChip
              clientId={client?.clientId || "client-settings"}
              name={client?.clientName || "Client Settings"}
            />
          )}
        </Stack>
        {isLoading ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={400}
            sx={{ mb: 2 }}
          />
        ) : (
          <Stack
            spacing={2}
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "background.paper",
            }}
          >
            {!isLoading && !client && (
              <Typography color="error">
                Unable to load client details.
              </Typography>
            )}

            {!!client && (
              <>
                <Stack spacing={2}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="client settings sections"
                    variant="fullWidth"
                  >
                    <Tab label="General" />
                    <Tab label="Email Whitelisting" />
                    <Tab label="Admin Users" />
                  </Tabs>

                  {tabValue === 0 && (
                    <ClientGeneralTab
                      initialClientName={client.clientName || ""}
                      onSave={handleSaveGeneral}
                      isSaving={isPending}
                    />
                  )}

                  {tabValue === 1 && (
                    <ClientWhitelistingTab
                      initialDomainWhiteList={client.domainWhiteList || []}
                      onSave={handleSaveWhitelist}
                      isSaving={isPending}
                    />
                  )}

                  {tabValue === 2 && (
                    <ClientAdminUsersTab clientId={clientId} />
                  )}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </MainLayout>
  );
};

export default ClientPage;
